/**
 * KAN-757: сохранение правки ячейки доски CRM.
 * Строка меняется в кеше доски сразу (оптимистично), ответ сервера заменяет
 * строку целиком. При ошибке поля, которые правились, возвращаются к прежним
 * значениям (остальные правки той же строки не трогаются), а `save` отдаёт
 * текст ошибки для показа у ячейки. Меняются все загруженные периоды доски.
 * После успешного сохранения обновляется блок "Требуют внимания" дашборда
 * (ключи ATTENTION_QUERY_KEY, кроме самой доски: её строку уже дал сервер).
 * KAN-760: правка этапа (stage_id), этап для оптимистичного показа из board.stages.
 * KAN-880: так же меняются загруженные страницы таблицы лидов (CRM_LEADS_QUERY_KEY):
 * ответ сервера сливается со строкой. Колонку и дни строки считает сервер, а
 * ответ PATCH их не содержит: после смены этапа или менеджера таблица
 * перезапрашивается, после остальных правок нет (как и доска).
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { crmApi } from '@/services/api/crm';
import { ATTENTION_QUERY_KEY, CRM_BOARD_QUERY_KEY, CRM_LEADS_QUERY_KEY } from '@/services/api/queryKeys';
import { useAssignableManagers } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { findLeadRow, replaceLeadRow, type CrmLeadsData } from '@/lib/crmLeads';
import { applyCrmPatch, replaceCrmRow } from '@/lib/crmRowEdit';
import type { CrmBoard, CrmRow, CrmRowPatch } from '@/types/crm';

const ERROR_FALLBACK = 'Не удалось сохранить';

/** Поля строки, которые затрагивает правка (для отката). */
const PATCH_FIELDS: Record<keyof CrmRowPatch, keyof CrmRow> = {
  phone: 'phone',
  clinic: 'clinic',
  city: 'city',
  contact_name: 'contact_name',
  telegram_username: 'telegram',
  manager_id: 'manager',
  next_contact_at: 'next_contact_at',
  comment: 'comment',
  stage_id: 'stage',
  // KAN-783: словарь атрибутов откатывается целиком.
  attributes: 'attributes',
};

const hasPrefix = (key: readonly unknown[], prefix: readonly unknown[]): boolean =>
  prefix.every((part, i) => key[i] === part);

/** Ключ доски CRM или таблицы лидов: их строку уже дал сервер, перезапрос не нужен. */
function isBoardKey(key: readonly unknown[]): boolean {
  return hasPrefix(key, CRM_BOARD_QUERY_KEY) || hasPrefix(key, CRM_LEADS_QUERY_KEY);
}

function findRow(boards: readonly (CrmBoard | undefined)[], clientId: string): CrmRow | undefined {
  for (const board of boards) {
    if (!board) continue;
    const found = [...board.attention, ...board.days.flatMap((d) => d.rows)].find((r) => r.client_id === clientId);
    if (found) return found;
  }
  return undefined;
}

/** KAN-880: правка меняет колонку или дни строки таблицы лидов (этап, менеджер). */
const changesLeadColumn = (patch: CrmRowPatch): boolean => 'stage_id' in patch || 'manager_id' in patch;

export function useCrmRowEdit() {
  const queryClient = useQueryClient();
  // KAN-828: все активные менеджеры (менеджер может передать клиента другому).
  const { data: managers } = useAssignableManagers();

  /** Сохранить правку; результат: текст ошибки или null. */
  const save = useCallback(
    async (clientId: string, patch: CrmRowPatch): Promise<string | null> => {
      await queryClient.cancelQueries({ queryKey: CRM_BOARD_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: CRM_LEADS_QUERY_KEY });
      const boards = queryClient.getQueriesData<CrmBoard>({ queryKey: CRM_BOARD_QUERY_KEY }).map(([, b]) => b);
      const leads = queryClient.getQueriesData<CrmLeadsData>({ queryKey: CRM_LEADS_QUERY_KEY }).map(([, d]) => d);
      const before = findRow(boards, clientId) ?? leads.map((d) => findLeadRow(d, clientId)).find(Boolean);
      const stages =
        boards.find((b) => b?.stages?.length)?.stages ?? leads.find((d) => d?.pages[0]?.stages?.length)?.pages[0]?.stages ?? [];
      const setBoard = (update: (row: CrmRow) => CrmRow) => {
        queryClient.setQueriesData<CrmBoard>({ queryKey: CRM_BOARD_QUERY_KEY }, (board) =>
          board ? replaceCrmRow(board, clientId, update) : board
        );
        queryClient.setQueriesData<CrmLeadsData>({ queryKey: CRM_LEADS_QUERY_KEY }, (data) =>
          data ? replaceLeadRow(data, clientId, update) : data
        );
      };

      setBoard((row) => applyCrmPatch(row, patch, managers, stages));
      try {
        const saved = await crmApi.updateClient(clientId, patch);
        setBoard(() => saved);
        void queryClient.invalidateQueries({
          queryKey: ATTENTION_QUERY_KEY,
          predicate: (query) => !isBoardKey(query.queryKey),
        });
        if (changesLeadColumn(patch)) void queryClient.invalidateQueries({ queryKey: CRM_LEADS_QUERY_KEY });
        return null;
      } catch (err) {
        if (before) {
          const keys = (Object.keys(patch) as (keyof CrmRowPatch)[]).map((k) => PATCH_FIELDS[k]);
          const restore: Partial<CrmRow> = Object.fromEntries(keys.map((key) => [key, before[key]]));
          setBoard((row) => ({ ...row, ...restore }));
        }
        return extractErrorMessage(err, ERROR_FALLBACK);
      }
    },
    [queryClient, managers]
  );

  return { save, managers };
}
